import { useEffect, useState } from "react";
import { theme, card, inputStyle, btnPrimary, emptyFrame } from "../theme";
import { api } from "../api/client";
import { IndexTabs } from "../components/Drawing";
import { useConsultantShell } from "./consultant/ConsultantShellContext";
import type { StaffMember } from "./consultant/workspace";
import { InviteClientModal } from "../components/InviteModals";
import { frameworkLabel } from "../nav/frameworkMeta";

// ── Types ─────────────────────────────────────────────────────────────────

interface UserRecord {
  id: string;
  email: string;
  displayName: string;
  role: "client" | "consultant";
  isActive: boolean;
  organizationId: string | null;
  organization: { id: string; name: string } | null;
  primaryGrade: string | null;
  signInMethod: "microsoft" | "google" | "saml" | "password" | "none";
  totpEnabled: boolean;
  canActAsAuditor: boolean;
  auditorBureauId: string | null;
}

interface OrgOption { id: string; name: string }
interface BureauOption { id: string; name: string; bureauStatus: string }

interface OrgSummary {
  id: string;
  name: string;
  sector: string | null;
  createdAt: string;
  tier: string;
  frameworks: string[];
  latestScore: number | null;
  userCount: number;
}

type RoleKey = "client" | "consultant";
type ModalTab = "consultants" | "organisations";

const ROLE_LABELS: Record<RoleKey, string> = { client: "Client", consultant: "Consultant" };

const FW_SHORT: Record<string, string> = {
  iso27001: "27001", iso9001: "9001", iso42001: "42001",
  soc2: "SOC 2", cyber_essentials: "CE", cyber_essentials_plus: "CE+", pci_dss: "PCI",
  iasme_ca_sole_trader: "IASME ST", iasme_ca_micro: "IASME M",
  iasme_ca_small: "IASME S", iasme_ca_medium_large: "IASME M/L",
};

interface FormState {
  email: string;
  displayName: string;
  role: RoleKey;
  organizationId: string;
  primaryGrade: string;
}

const emptyForm = (role: RoleKey): FormState => ({
  email: "", displayName: "", role, organizationId: "", primaryGrade: "",
});

// ── Score colour ──────────────────────────────────────────────────────────

function scoreColor(s: number | null) {
  if (s === null) return theme.color.faint;
  if (s >= 75) return theme.color.successText;
  if (s >= 50) return theme.color.warningText;
  return theme.color.dangerText;
}

// ── Inline deactivate confirmation ────────────────────────────────────────
// People are deactivated, never deleted: their name stays on everything they did.

function DeactivateRow({ label, onConfirm, onCancel }: {
  label: string; onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <div style={{
      background: theme.color.dangerBg, border: `1px solid ${theme.color.danger}`,
      borderTop: "none", borderRadius: "0 0 3px 3px",
      padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, fontSize: 13,
    }}>
      <span style={{ color: theme.color.dangerText, flex: 1 }}>
        Deactivate <strong>{label}</strong>? They are signed out everywhere and can no longer sign in.
        Their work keeps their name, and you can reactivate them later.
      </span>
      <button onClick={onConfirm} style={{ ...btnPrimary, background: theme.color.danger }}>
        Deactivate
      </button>
      <button onClick={onCancel} style={{
        background: "none", border: `1px solid ${theme.color.border}`,
        borderRadius: 3, padding: "9px 14px", fontSize: 13,
        color: theme.color.muted, cursor: "pointer",
      }}>
        Cancel
      </button>
    </div>
  );
}

// ── Name-confirmation org delete ──────────────────────────────────────────

function OrgDeleteConfirm({ orgName, onConfirm, onCancel, submitting }: {
  orgName: string; onConfirm: () => void; onCancel: () => void; submitting: boolean;
}) {
  const [value, setValue] = useState("");
  const matches = value.trim() === orgName;

  return (
    <div style={{
      background: theme.color.dangerBg, border: `1px solid ${theme.color.danger}`,
      borderTop: "none", borderRadius: "0 0 3px 3px",
      padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ fontSize: 12, color: theme.color.dangerText, lineHeight: 1.5 }}>
        This will permanently delete the organisation and <strong>all associated data</strong> — users,
        documents, compliance records, invoices, and audit history. Type the organisation name to confirm.
      </div>
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={orgName}
        style={{ ...inputStyle, width: "100%", boxSizing: "border-box" as const }}
      />
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={{
          background: "none", border: `1px solid ${theme.color.border}`,
          borderRadius: 3, padding: "7px 14px", fontSize: 12,
          color: theme.color.muted, cursor: "pointer",
        }}>
          Cancel
        </button>
        <button
          disabled={!matches || submitting}
          onClick={onConfirm}
          style={{
            ...btnPrimary,
            background: theme.color.danger,
            opacity: matches && !submitting ? 1 : 0.4,
            cursor: matches && !submitting ? "pointer" : "not-allowed",
          }}
        >
          {submitting ? "Deleting…" : "Delete organisation"}
        </button>
      </div>
    </div>
  );
}

// ── Users page (/consultant/settings/users) ───────────────────────────────
// Team & Client Management. Consultants (firm:users on the API) and the client
// organisations the firm manages, with the delete flows behind name
// confirmation. Rendered as a page in the consultant shell; the file keeps its
// historical name.

export function ConsultantUsersPage() {
  const [tab, setTab] = useState<ModalTab>("consultants");

  const [users, setUsers] = useState<UserRecord[]>([]);
  const [allOrgs, setAllOrgs] = useState<OrgOption[]>([]);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [error, setError] = useState("");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingRole, setAddingRole] = useState<RoleKey | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [deletingOrgId, setDeletingOrgId] = useState<string | null>(null);
  const [deletingOrgSubmitting, setDeletingOrgSubmitting] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm("client"));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [showInviteClient, setShowInviteClient] = useState(false);
  // Bureaux linked to the firm, for the auditor-access control (PATCH /consultant/users/:id/auditor-access).
  const [bureaus, setBureaus] = useState<BureauOption[]>([]);
  const [search, setSearch] = useState("");
  // Each consultant's clients and this month's booked hours (GET /consultant/workspace/staff).
  const [workload, setWorkload] = useState<Map<string, StaffMember>>(new Map());
  const shell = useConsultantShell();

  const loadUsers = () => {
    setLoading(true);
    api.get<{ users: UserRecord[]; allOrgs: OrgOption[] }>("/consultant/users")
      .then((d) => { setUsers(d.users); setAllOrgs(d.allOrgs); })
      .catch(() => setError("Failed to load users."))
      .finally(() => setLoading(false));
  };

  const loadOrgs = () => {
    setOrgsLoading(true);
    api.get<OrgSummary[]>("/consultant/clients")
      .then(setOrgs)
      .catch(() => setError("Failed to load organisations."))
      .finally(() => setOrgsLoading(false));
  };

  useEffect(loadUsers, []);
  useEffect(() => {
    api.get<{ bureaus: BureauOption[] }>("/consultant/auditor-bureaus")
      .then((d) => setBureaus(d.bureaus))
      .catch(() => undefined); // no bureau, no auditor-access control
  }, []);
  useEffect(() => {
    api.get<{ staff: StaffMember[] }>("/consultant/workspace/staff")
      .then((d) => setWorkload(new Map(d.staff.map((s) => [s.id, s]))))
      .catch(() => undefined); // the workload line is extra; the list still works without it
  }, [users]);

  useEffect(() => {
    if (tab === "organisations" && orgs.length === 0 && !orgsLoading) loadOrgs();
  }, [tab]);

  // ── User form ────────────────────────────────────────────────────────────

  const openAdd = (role: RoleKey) => {
    setEditingId(null); setAddingRole(role); setForm(emptyForm(role)); setFormError("");
  };
  const openEdit = (u: UserRecord) => {
    setAddingRole(null); setEditingId(u.id);
    setForm({ email: u.email, displayName: u.displayName, role: u.role, organizationId: u.organizationId ?? "", primaryGrade: u.primaryGrade ?? "" });
    setFormError("");
  };
  const cancelForm = () => { setEditingId(null); setAddingRole(null); };

  const saveForm = async () => {
    setSaving(true); setFormError("");
    try {
      if (editingId) {
        const body: Record<string, unknown> = { email: form.email, displayName: form.displayName };
        if (form.role === "client") body.organizationId = form.organizationId || null;
        if (form.role === "consultant") body.primaryGrade = form.primaryGrade || null;
        await api.patch(`/consultant/users/${editingId}`, body);
      } else {
        const body: Record<string, unknown> = { email: form.email, displayName: form.displayName, role: form.role };
        if (form.role === "client") body.organizationId = form.organizationId || undefined;
        await api.post("/consultant/users", body);
      }
      cancelForm(); loadUsers();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reactivate = async (u: UserRecord) => {
    try {
      await api.patch(`/consultant/users/${u.id}`, { isActive: true });
      loadUsers();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to reactivate the account.");
    }
  };

  const setAuditorAccess = async (u: UserRecord, bureauId: string) => {
    try {
      await api.patch(`/consultant/users/${u.id}/auditor-access`, bureauId
        ? { canActAsAuditor: true, auditorBureauId: bureauId }
        : { canActAsAuditor: false });
      loadUsers();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to change auditor access.");
    }
  };

  const resetMfa = async (u: UserRecord) => {
    if (!confirm(`Reset the authenticator app for ${u.displayName}? Only do this once you have confirmed it is really them. They will be signed out everywhere and set up a new app after their next password and emailed code.`)) return;
    try {
      await api.post(`/consultant/users/${u.id}/mfa-reset`);
      loadUsers();
    } catch {
      setFormError("Failed to reset the authenticator. Please try again.");
    }
  };

  const confirmDeactivateUser = async (id: string) => {
    try {
      await api.del(`/consultant/users/${id}`);
      setDeletingUserId(null);
      loadUsers();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Deactivation failed");
    }
  };

  const confirmDeleteOrg = async (id: string) => {
    setDeletingOrgSubmitting(true);
    try {
      await api.del(`/consultant/clients/${id}`);
      setDeletingOrgId(null);
      setOrgs((prev) => prev.filter((o) => o.id !== id));
      // The shared client list (sidebar, portfolio, recent clients) forgets it at once.
      shell.forgetClient(id);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setDeletingOrgSubmitting(false);
    }
  };

  // ── User form renderer ────────────────────────────────────────────────────

  const renderForm = (role: RoleKey, isEdit: boolean) => (
    <div style={{
      background: theme.color.bg, border: `1px solid ${theme.color.border}`,
      borderRadius: 3, padding: 16, marginTop: 8,
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.consultant, marginBottom: 2 }}>
        {isEdit ? "Edit User" : `Add ${ROLE_LABELS[role]}`}
      </div>
      {formError && <div style={{ color: theme.color.dangerText, fontSize: 12 }}>{formError}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 11, color: theme.color.muted }}>Display Name</label>
          <input style={inputStyle} value={form.displayName}
            onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
            placeholder="Full name" />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 11, color: theme.color.muted }}>Email</label>
          <input style={inputStyle} type="email" value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="user@example.com" />
        </div>
      </div>

      {role === "client" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 11, color: theme.color.muted }}>Client Company</label>
          <select style={{ ...inputStyle, cursor: "pointer" }} value={form.organizationId}
            onChange={(e) => setForm((f) => ({ ...f, organizationId: e.target.value }))}>
            <option value="">— Select company —</option>
            {allOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      )}

      {role === "consultant" && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, color: theme.color.muted }}>Primary Grade</label>
            <select
              style={{ ...inputStyle, cursor: "pointer" }}
              value={form.primaryGrade}
              onChange={(e) => setForm((f) => ({ ...f, primaryGrade: e.target.value }))}
            >
              <option value="">— Not set (cannot be booked by clients) —</option>
              <option value="senior">Senior — strategic advisory, architecture, senior risk reviews</option>
              <option value="consultant">Consultant — control implementation, documentation, gap analysis</option>
              <option value="junior">Junior — evidence gathering, template population, admin support</option>
            </select>
            <div style={{ fontSize: 11, color: theme.color.faint }}>
              A senior can also be booked for consultant or junior work. A consultant can also be booked for junior work.
            </div>
          </div>
          <div style={{
            fontSize: 12, color: theme.color.muted, padding: "8px 12px",
            background: theme.color.consultantBg, borderRadius: 3,
          }}>
            This user will be added to your consultancy and can access all client accounts.
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <button onClick={saveForm} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={cancelForm} style={{
          background: "none", border: `1px solid ${theme.color.border}`,
          borderRadius: 3, padding: "9px 18px", fontSize: 13, color: theme.color.muted,
          cursor: "pointer", fontWeight: 600,
        }}>
          Cancel
        </button>
      </div>
    </div>
  );

  // ── User section renderer ─────────────────────────────────────────────────

  const renderUserSection = (role: RoleKey) => {
    const needle = search.trim().toLowerCase();
    const roleUsers = users.filter((u) => u.role === role && (!needle || u.displayName.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle)));
    const isAdding = addingRole === role;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 12, color: theme.color.muted }}>
            {roleUsers.length === 0 ? `No ${ROLE_LABELS[role].toLowerCase()} users yet.` : `${roleUsers.length} ${ROLE_LABELS[role].toLowerCase()}${roleUsers.length !== 1 ? "s" : ""}`}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email" aria-label="Search users"
              style={{ ...inputStyle, width: 200, padding: "5px 10px", fontSize: 12 }} />
            <button onClick={() => openAdd(role)} disabled={isAdding} style={{
              background: "none", border: `1px solid ${theme.color.consultant}`,
              borderRadius: 3, padding: "5px 12px", fontSize: 12,
              color: theme.color.consultant, cursor: "pointer", fontWeight: 600,
              opacity: isAdding ? 0.4 : 1,
            }}>
              + Add {ROLE_LABELS[role]}
            </button>
          </div>
        </div>

        {isAdding && renderForm(role, false)}

        {roleUsers.length === 0 && !isAdding && (
          <div style={{ ...emptyFrame, fontSize: 12, color: theme.color.subtle, padding: "24px 16px" }}>
            No {ROLE_LABELS[role].toLowerCase()} users yet.
          </div>
        )}

        {roleUsers.map((u) => {
          const isEditing = editingId === u.id;
          const isDeleting = deletingUserId === u.id;
          const load = workload.get(u.id);
          const contextLabel = role === "client"
            ? (u.organization?.name ?? "No company")
            : load
              ? `${load.assignedCount} client${load.assignedCount !== 1 ? "s" : ""}${load.leadCount ? ` (${load.leadCount} lead)` : ""} · ${load.bookedHoursThisMonth} h booked this month${load.pendingRequests ? ` · ${load.pendingRequests} waiting` : ""}`
              : "All clients";

          return (
            <div key={u.id} style={{ display: "flex", flexDirection: "column" }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                background: theme.color.bg, border: `1px solid ${theme.color.border}`,
                borderRadius: isEditing || isDeleting ? "3px 3px 0 0" : 3,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: u.isActive ? theme.color.heading : theme.color.subtle }}>
                    {u.displayName}
                    {!u.isActive && <span style={{ marginLeft: 8, fontSize: 10, color: theme.color.faint }}>[deactivated]</span>}
                  </div>
                  <div style={{ fontSize: 11, color: theme.color.subtle, fontVariantNumeric: "tabular-nums" }}>{u.email}</div>
                </div>

                <span style={{
                  fontSize: 10, padding: "2px 8px", borderRadius: 4,
                  background: theme.color.panel, color: theme.color.muted,
                  border: `1px solid ${theme.color.border}`, whiteSpace: "nowrap",
                }}>
                  {contextLabel}
                </span>

                {role === "consultant" && (
                  <span style={{
                    fontSize: 10, padding: "2px 8px", borderRadius: 4, whiteSpace: "nowrap", fontWeight: 600,
                    background: u.primaryGrade ? theme.color.consultantBg : theme.color.panelAlt,
                    color: u.primaryGrade ? theme.color.consultant : theme.color.faint,
                    border: `1px solid ${u.primaryGrade ? theme.color.consultant + "44" : theme.color.border}`,
                  }}>
                    {u.primaryGrade
                      ? u.primaryGrade.charAt(0).toUpperCase() + u.primaryGrade.slice(1)
                      : "No grade set"}
                  </span>
                )}

                {role === "consultant" && bureaus.length > 0 && u.isActive && (
                  <select
                    aria-label={`Auditor access for ${u.displayName}`}
                    title="Lets this consultant work in the auditor portal for one of your bureaux"
                    value={u.canActAsAuditor ? (u.auditorBureauId ?? "") : ""}
                    onChange={(e) => setAuditorAccess(u, e.target.value)}
                    style={{ ...inputStyle, width: 170, padding: "3px 8px", fontSize: 11 }}
                  >
                    <option value="">No auditor access</option>
                    {bureaus.map((b) => (
                      <option key={b.id} value={b.id}>Auditor: {b.name}{b.bureauStatus !== "active" ? " (not active)" : ""}</option>
                    ))}
                  </select>
                )}

                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => openEdit(u)} style={{
                    background: "none", border: `1px solid ${theme.color.border}`,
                    borderRadius: 3, padding: "4px 10px", fontSize: 11,
                    color: theme.color.muted, cursor: "pointer",
                  }}>
                    Edit
                  </button>
                  {u.signInMethod === "password" && u.totpEnabled && (
                    <button onClick={() => resetMfa(u)} title="Clear their authenticator app so they can enrol a new one" style={{
                      background: "none", border: `1px solid ${theme.color.border}`,
                      borderRadius: 3, padding: "4px 10px", fontSize: 11,
                      color: theme.color.muted, cursor: "pointer",
                    }}>
                      Reset MFA
                    </button>
                  )}
                  {u.isActive ? (
                    <button onClick={() => setDeletingUserId(u.id)} style={{
                      background: "none", border: `1px solid ${theme.color.danger}`,
                      borderRadius: 3, padding: "4px 10px", fontSize: 11,
                      color: theme.color.dangerText, cursor: "pointer",
                    }}>
                      Deactivate
                    </button>
                  ) : (
                    <button onClick={() => reactivate(u)} style={{
                      background: "none", border: `1px solid ${theme.color.success}`,
                      borderRadius: 3, padding: "4px 10px", fontSize: 11,
                      color: theme.color.successText, cursor: "pointer",
                    }}>
                      Reactivate
                    </button>
                  )}
                </div>
              </div>

              {isEditing && (
                <div style={{
                  background: theme.color.bg, border: `1px solid ${theme.color.border}`,
                  borderTop: "none", borderRadius: "0 0 3px 3px", padding: "0 16px 16px",
                }}>
                  {renderForm(u.role, true)}
                </div>
              )}

              {isDeleting && (
                <DeactivateRow
                  label={u.displayName}
                  onConfirm={() => confirmDeactivateUser(u.id)}
                  onCancel={() => setDeletingUserId(null)}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Organisations tab ─────────────────────────────────────────────────────

  const renderOrgsTab = () => {
    if (orgsLoading) return <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading organisations…</div>;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 12, color: theme.color.muted }}>
            {orgs.length === 0 ? "No client organisations yet." : `${orgs.length} organisation${orgs.length !== 1 ? "s" : ""}`}
          </div>
          <button onClick={() => setShowInviteClient(true)} style={{
            background: "none", border: `1px solid ${theme.color.accent}`,
            borderRadius: 3, padding: "5px 12px", fontSize: 12,
            color: theme.color.accentText, cursor: "pointer", fontWeight: 600,
          }}>
            + New Client Environment
          </button>
        </div>

        {orgs.length === 0 && (
          <div style={{ ...emptyFrame, fontSize: 12, color: theme.color.subtle, padding: "24px 16px" }}>
            No client organisations yet. Invite a client to get started.
          </div>
        )}

        {orgs.map((org) => {
          const isDeleting = deletingOrgId === org.id;

          return (
            <div key={org.id} style={{ display: "flex", flexDirection: "column" }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                background: theme.color.bg, border: `1px solid ${theme.color.border}`,
                borderRadius: isDeleting ? "3px 3px 0 0" : 3,
              }}>
                {/* Name + sector — keeps a readable width however many standards sit beside it */}
                <div style={{ flex: "1 1 160px", minWidth: 120 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{org.name}</div>
                  {org.sector && <div style={{ fontSize: 11, color: theme.color.faint }}>{org.sector}</div>}
                </div>

                {/* Frameworks — wrap onto further rows rather than squeezing the name */}
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end", flex: "0 1 260px", minWidth: 0 }}>
                  {org.frameworks.length === 0 ? (
                    <span style={{ fontSize: 10, color: theme.color.faint }}>No frameworks</span>
                  ) : org.frameworks.map((f) => (
                    <span key={f} style={{
                      fontSize: 10, padding: "2px 6px", borderRadius: 4, lineHeight: 1.4, whiteSpace: "nowrap",
                      background: theme.color.panel, color: theme.color.muted,
                      border: `1px solid ${theme.color.border}`,
                    }}>
                      {FW_SHORT[f] ?? frameworkLabel(f)}
                    </span>
                  ))}
                </div>

                {/* Score */}
                <div style={{ minWidth: 36, textAlign: "right" }}>
                  {org.latestScore !== null ? (
                    <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(org.latestScore) }}>
                      {org.latestScore}%
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, color: theme.color.faint }}>—</span>
                  )}
                </div>

                {/* Users count */}
                <span style={{
                  fontSize: 11, color: theme.color.muted, whiteSpace: "nowrap",
                }}>
                  {org.userCount} user{org.userCount !== 1 ? "s" : ""}
                </span>

                {/* Tier */}
                <span style={{
                  fontSize: 10, padding: "2px 8px", borderRadius: 4,
                  background: theme.color.consultantBg, color: theme.color.consultant,
                  border: `1px solid ${theme.color.border}`, whiteSpace: "nowrap", fontWeight: 600,
                }}>
                  {org.tier}
                </span>

                <button
                  onClick={() => setDeletingOrgId(org.id)}
                  style={{
                    background: "none", border: `1px solid ${theme.color.danger}`,
                    borderRadius: 3, padding: "4px 10px", fontSize: 11,
                    color: theme.color.dangerText, cursor: "pointer",
                  }}
                >
                  Delete
                </button>
              </div>

              {isDeleting && (
                <OrgDeleteConfirm
                  orgName={org.name}
                  onConfirm={() => confirmDeleteOrg(org.id)}
                  onCancel={() => setDeletingOrgId(null)}
                  submitting={deletingOrgSubmitting}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Tab counts ────────────────────────────────────────────────────────────

  // Active consultants only: a deactivated colleague is listed, never counted.
  const consultantCount = users.filter((u) => u.role === "consultant" && u.isActive).length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="page-pad" style={{ fontFamily: theme.font }}>
      <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        Team & Client Management
      </h1>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: theme.color.subtle }}>
        Manage consultants, client users and organisations.
      </p>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "8px 24px 0" }}>
          <IndexTabs
            ground="var(--c-panel)"
            style={{ marginBottom: 0 }}
            value={tab}
            onChange={setTab}
            items={[
              { id: "consultants", label: "Consultants", count: loading ? undefined : consultantCount },
              { id: "organisations", label: "Organisations", count: orgs.length > 0 ? orgs.length : undefined },
            ]}
          />
        </div>

        <div className="page-pad">
          {error && <div style={{ color: theme.color.dangerText, fontSize: 13, marginBottom: 12 }}>{error}</div>}

          {loading && tab !== "organisations" ? (
            <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading</div>
          ) : (
            <>
              {tab === "consultants" && renderUserSection("consultant")}
              {tab === "organisations" && renderOrgsTab()}
            </>
          )}
        </div>
      </div>

      {showInviteClient && (
        <InviteClientModal onClose={() => {
          setShowInviteClient(false);
          loadUsers();
          if (tab === "organisations") loadOrgs();
        }} />
      )}
    </div>
  );
}
